import axios, { type AxiosRequestConfig } from "axios";

import { t } from "@/locales/config";
import { AdminApiError } from "./api-error";
import { createAuthorizationHandler, createScopeUpdateHandler } from "../auth/authorization";
import { getAdminAuthToken } from "../auth/auth-token";
import { apiBaseUrl } from "@/utils";

const adminHttp = axios.create({
  baseURL: apiBaseUrl,
  timeout: 12_000,
});

adminHttp.interceptors.request.use(async (config) => {
  config.headers.Accept = "application/json";
  const token = await getAdminAuthToken();
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let navigationStarted = false;

function navigateAuthorization(url: string) {
  if (navigationStarted) {
    return;
  }
  navigationStarted = true;
  window.open(url, "_top");
}

export const handleScopeUpdate = createScopeUpdateHandler(navigateAuthorization);

const authorization = createAuthorizationHandler({
  apiOrigin: apiBaseUrl,
  getToken: getAdminAuthToken,
  navigate: navigateAuthorization,
});

interface AdminResponseEnvelope {
  code: number;
  msg?: unknown;
  data?: unknown;
}

function isAdminResponseEnvelope(input: unknown): input is AdminResponseEnvelope {
  return (
    typeof input === "object" && input !== null && "code" in input && typeof input.code === "number"
  );
}

export function unwrapAdminResponse<T>(input: T | AdminResponseEnvelope): T {
  if (!isAdminResponseEnvelope(input)) return input as T;

  if (input.code !== 200) {
    const redirecting = input.code === 15 || input.code === 16;
    const message = typeof input.msg === "string" ? input.msg : "";
    throw new AdminApiError(
      redirecting ? t("ShopifyAuthorizationRequired") : message || t("BlyCareCouldNotComplete"),
      input.code,
      redirecting ? "redirect" : "business",
    );
  }

  return input.data as T;
}

adminHttp.interceptors.response.use(async (response) => {
  await authorization(response.data);
  response.data = unwrapAdminResponse(response.data);
  return response;
});

/** Orval mutator: all generated endpoints receive the globally unwrapped business payload. */
export async function adminRequest<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> {
  const response = await adminHttp.request<T>({ ...config, ...options });
  return response.data;
}
